import { lazy, useState, type ComponentType } from 'react';

type ComponentModule<P> = { default: ComponentType<P> };

export type PreloadableComponent<P> = ComponentType<P> & {
  /** Starts loading the module ahead of the first render. */
  preload: () => Promise<ComponentModule<P>>;
};

/**
 * React.lazy with a `preload()` hook.
 *
 * React.lazy suspends on an instance's first render even when its module is
 * already cached, which flashes the nearest Suspense fallback. Once this
 * module has loaded (via `preload()` or an earlier render), new instances
 * render the component directly and never suspend.
 */
export function lazyWithPreload<P extends object>(
  loader: () => Promise<ComponentModule<P>>
): PreloadableComponent<P> {
  let loaded: ComponentType<P> | null = null;
  let pending: Promise<ComponentModule<P>> | null = null;

  const load = () => {
    if (!pending) {
      pending = loader()
        .then((mod) => {
          loaded = mod.default;
          return mod;
        })
        .catch((error) => {
          // Let the next preload/render retry instead of caching the failure
          pending = null;
          throw error;
        });
    }
    return pending;
  };

  const LazyComponent = lazy(load);

  const Preloadable = (props: P) => {
    // Picked once per instance: switching type on a later render would
    // remount the component and drop its state
    const [Component] = useState<ComponentType<P>>(
      () => loaded ?? LazyComponent
    );
    return <Component {...props} />;
  };

  return Object.assign(Preloadable, { preload: load });
}
